
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

interface AdminUser {
  id: string;
  email: string;
}

export const useAdminAuth = () => {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(false);

  const checkIfAdmin = async (email: string, userId: string) => {
    if (isCheckingAdmin) return null;
    
    setIsCheckingAdmin(true);
    try {
      const { data: isAdmin, error } = await supabase
        .rpc('check_admin_email', { email_to_check: email });

      if (!error && isAdmin) {
        return { id: userId, email };
      }
      return null;
    } catch (error) {
      console.error('Admin check error:', error);
      return null;
    } finally {
      setIsCheckingAdmin(false);
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        
        if (session?.user?.email && session?.user?.id) {
          // Only check admin status on sign in, not on every auth change
          if (event === 'SIGNED_IN') {
            const admin = await checkIfAdmin(session.user.email, session.user.id);
            setAdminUser(admin);
          }
        } else {
          setAdminUser(null);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user?.email && session?.user?.id) {
        checkIfAdmin(session.user.email, session.user.id).then(setAdminUser);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loginAdmin = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      
      // Use Supabase Auth to sign in - the admin check will be handled by onAuthStateChange
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error: error.message === 'Invalid login credentials' ? 'Identifiants invalides' : 'Erreur de connexion' };
      }

      if (data.user && data.session) {
        // Check admin status directly here for immediate feedback
        const admin = await checkIfAdmin(data.user.email || '', data.user.id);
        
        if (!admin) {
          await supabase.auth.signOut();
          return { success: false, error: 'Identifiants invalides' };
        }

        setAdminUser(admin);
        return { success: true, admin };
      } else {
        return { success: false, error: 'Erreur de connexion' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Erreur de connexion' };
    } finally {
      setIsLoading(false);
    }
  };

  const logoutAdmin = async () => {
    await supabase.auth.signOut();
    setAdminUser(null);
    setSession(null);
  };

  const isAdminLoggedIn = !!adminUser && !!session;

  return {
    adminUser,
    isLoading,
    loginAdmin,
    logoutAdmin,
    isAdminLoggedIn
  };
};
